import { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Paper, Grid, Alert, Box } from '@mui/material';
import { usePhrase, useRouter, useServerErrors } from '@/hooks';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IWalletNonMemberData,
    IWalletNonMemberRequestData,
} from '@/types/types/forms/wallet/wallet.interface';
import { SelectField } from '@/components/forms/inputs/SelectField/SelectField';
import { CheckboxField } from '@/components/forms/inputs/CheckBoxField/CheckboxField';
import { WalletPaymentDetailsFields } from '@/components/blocks/wallet/Payment/WalletPaymentDetailsFields/WalletPaymentDetailsFields';
import { PaymentAmount } from '@/components/blocks/wallet/PaymentAmount/PaymentAmount';
import { WALLET_NON_MEMBER_PHRASES } from '@/consts/phrases/walletPhrases';
import { walletService } from '@/services/api/walletService/walletService';
import { isArrayWithValues } from '@/utils/fp/array/array';
import { createSelectOptionsFromArray } from '@/utils/forms';
import { mutate } from 'swr';
import { config } from '@/config';
import { JSXFromString } from '@/utils';
import { useZipLocations } from '@/hooks/forms';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

const API_URL = config.api.url.wallet;
interface NonMemberPaymentDetailsFormProps {
    data: IWalletNonMemberData;
    dictionary: DefaultPageProps['dictionary'];
    token: string;
    setPaymentMethodId: (val: number) => void;
}

export const NonMemberPaymentDetailsForm = ({
    data,
    dictionary,
    token,
    setPaymentMethodId,
}: NonMemberPaymentDetailsFormProps) => {
    const { t } = usePhrase();
    const router = useRouter();

    const {
        isRegistrationAllowed,
        registrationType,
        paymentMethods,
        paymentForm: { fields: dataFields, states, footerNotesPhraseIds, paymentMethodId },
        paymentNotes,
    } = data;

    const defaultPaymentMethodId = paymentMethods[0].paymentMethodId;

    const currentPaymentMethod = Number(paymentMethodId || defaultPaymentMethodId); // Convert to Number as API return a string

    const firstTransaction = data?.payments?.[0];

    const paymentOptions = createSelectOptionsFromArray({
        label: 'phraseId',
        value: 'paymentMethodId',
    })(paymentMethods);

    const defaultValues: Partial<IWalletNonMemberRequestData> = (() => {
        if (!data || !isArrayWithValues(dataFields)) return {};
        return dataFields.reduce(
            (acc, field) => ({ ...acc, [field.id]: field.value ?? undefined }),
            {},
        );
    })();

    const { handleSubmit, control, watch, setValue, getValues, formState, setError, clearErrors } =
        useForm<Partial<IWalletNonMemberRequestData>>({
            mode: 'onChange',
            values: {
                ...defaultValues,
                paymentMethodId: currentPaymentMethod,
                openRegistration: isRegistrationAllowed,
            },
        });

    const { errors, isSubmitting } = formState;

    const [zipValue, countryCodeValue] = watch(['zip', 'countryCode']) as [string, string];

    const { statesList, citiesList, onZipChange } = useZipLocations<
        Partial<IWalletNonMemberRequestData>
    >({
        zipValue,
        countryCodeValue,
        setValue,
    });

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const handlePaymentMethodChange = (value: number | FormEvent<HTMLDivElement>) => {
        if (typeof value === 'number') {
            setPaymentMethodId(value);
        }
    };

    const handleSubmitData = async (vals: Partial<IWalletNonMemberRequestData>) => {
        const { openRegistration, ...formData } = vals;
        try {
            await walletService.submitNonMemberPayment(token, formData);

            if (isRegistrationAllowed && openRegistration) {
                router.push(`/?reg_type=${registrationType}&token=${token}&hh_id=32773&s=1`);
            } else {
                // re-fetch data with and without paymentMethodId to resolve default paymentMethodId and changed paymentMethodId
                await mutate([`${API_URL}/non-member-payment`, `${token}`, undefined]);
                await mutate([`${API_URL}/non-member-payment`, `${token}`, paymentMethodId]);
            }
        } catch (err) {
            handleServerErrors(err);
        }
    };

    return (
        <Paper sx={{ p: 3, mt: 3 }}>
            <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
                <Grid container sx={{ mt: 2 }}>
                    <FormGridErrors
                        errors={serverErrors}
                        gridSx={{ mt: -2, mb: 4 }}
                        generalErrorId="server_error_message_box"
                    />
                    {isArrayWithValues(paymentNotes) &&
                        paymentNotes.map((noteItem) => (
                            <Grid
                                size={12}
                                key={noteItem.phraseId}
                                tabIndex={0}
                                sx={{ mt: -2, mb: 4 }}
                            >
                                <Alert severity="info" icon={false}>
                                    {JSXFromString(
                                        t(noteItem.phraseId, {
                                            replaceTextVars: noteItem.phrasePlaceholders,
                                        }),
                                    )}
                                </Alert>
                            </Grid>
                        ))}
                    <WalletPaymentDetailsFields
                        dataFields={dataFields}
                        dictionary={dictionary}
                        states={states}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        statesList={statesList}
                        citiesList={citiesList}
                        onZipChange={onZipChange}
                        prependField={
                            <>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SelectField
                                        control={control}
                                        name="paymentMethodId"
                                        label={WALLET_NON_MEMBER_PHRASES.PAYMENT_METHOD_LABEL}
                                        onChange={handlePaymentMethodChange}
                                        data={paymentOptions}
                                        required
                                        fullWidth
                                        error={errors?.paymentMethodId}
                                    />
                                </Grid>
                                <Grid
                                    size={{ xs: 12, sm: 6 }}
                                    sx={{
                                        alignItems: 'center',
                                        order: [-1, 0],
                                        mb: [1, 0],
                                        mt: [-2, 0],
                                    }}
                                >
                                    <Box sx={{ mt: [0, 0.5] }}>
                                        <PaymentAmount transactionData={firstTransaction} oneRow />
                                    </Box>
                                </Grid>
                            </>
                        }
                    />
                    {isRegistrationAllowed && (
                        <Grid size={12} sx={{ mt: 1 }}>
                            <CheckboxField
                                control={control}
                                name="openRegistration"
                                label={WALLET_NON_MEMBER_PHRASES.OPEN_REGISTRATION_TEXT}
                                error={errors?.openRegistration}
                            />
                        </Grid>
                    )}
                    {isArrayWithValues(footerNotesPhraseIds) && (
                        <>
                            {footerNotesPhraseIds.map((item) => (
                                <Alert severity="info" icon={false} key={item}>
                                    {t(Number(item))}
                                </Alert>
                            ))}
                        </>
                    )}
                </Grid>
                <Grid
                    container
                    sx={{
                        justifyContent: 'flex-end',
                        marginTop: 2,
                    }}
                >
                    <Button
                        type="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        variant="contained"
                    >
                        {t(WALLET_NON_MEMBER_PHRASES.SUBMIT_PAYMENT_BUTTON)}
                    </Button>
                </Grid>
            </form>
        </Paper>
    );
};
